/**
 * Content Manager - API Utilities
 *
 * Utility functions for API calls including retry logic, offline handling,
 * and error management.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ApiError extends Error {
  statusCode?: number;
  isNetworkError?: boolean;
  isOffline?: boolean;
}

export interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: ApiError, attempt: number) => boolean;
}

// =============================================================================
// OFFLINE DETECTION
// =============================================================================

/**
 * Check if the browser is currently online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Subscribe to online/offline events
 */
export function subscribeToNetworkStatus(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

// =============================================================================
// RETRY LOGIC
// =============================================================================

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  shouldRetry: (error: ApiError, attempt: number) => {
    // Don't retry if offline
    if (!isOnline()) return false;

    // Don't retry client errors (4xx) except 429 (rate limit)
    if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
      return error.statusCode === 429;
    }

    // Retry network errors and server errors (5xx)
    return error.isNetworkError || (error.statusCode !== undefined && error.statusCode >= 500);
  },
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter
  return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute a function with automatic retry logic
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const { maxRetries, baseDelay, maxDelay, shouldRetry } = {
    ...DEFAULT_RETRY_CONFIG,
    ...config,
  };

  let lastError: ApiError = new Error('Unknown error') as ApiError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Check if offline before attempting
      if (!isOnline()) {
        const offlineError = new Error('No internet connection') as ApiError;
        offlineError.isOffline = true;
        throw offlineError;
      }

      return await fn();
    } catch (error) {
      lastError = error as ApiError;

      // Check if we should retry
      if (attempt < maxRetries && shouldRetry(lastError, attempt)) {
        const delay = calculateDelay(attempt, baseDelay, maxDelay);
        console.warn(
          `API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(delay)}ms:`,
          lastError.message
        );
        await sleep(delay);
        continue;
      }

      // No more retries
      break;
    }
  }

  throw lastError;
}

// =============================================================================
// FETCH WRAPPER
// =============================================================================

interface FetchOptions extends RequestInit {
  timeout?: number;
}

/**
 * Enhanced fetch with timeout support
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { timeout = 30000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`) as ApiError;
      error.statusCode = response.status;
      throw error;
    }

    return response;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      const timeoutError = new Error('Request timed out') as ApiError;
      timeoutError.isNetworkError = true;
      throw timeoutError;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = error as ApiError;
      networkError.isNetworkError = true;
      throw networkError;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// =============================================================================
// ERROR HELPERS
// =============================================================================

/**
 * Create a user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  if (error instanceof Error) {
    const apiError = error as ApiError;

    if (apiError.isOffline) {
      return 'You appear to be offline. Please check your internet connection.';
    }

    if (apiError.isNetworkError) {
      return 'Unable to connect to the server. Please try again.';
    }

    if (apiError.statusCode) {
      switch (apiError.statusCode) {
        case 400:
          return 'Invalid request. Please check your input.';
        case 401:
          return 'Your session has expired. Please sign in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
        case 502:
        case 503:
          return 'Server error. Please try again later.';
        default:
          return apiError.message || 'An error occurred';
      }
    }

    return apiError.message || 'An error occurred';
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unknown error occurred';
}

/**
 * Check if an error is a network-related error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return Boolean(apiError.isNetworkError || apiError.isOffline);
  }
  return false;
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return apiError.statusCode === 401 || apiError.statusCode === 403;
  }
  return false;
}
